import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  Input,
  Button,
  Text,
  FormControl,
  FormLabel,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { getMultiFactorResolver, MultiFactorInfo, MultiFactorResolver, signInWithCustomToken, signInWithEmailAndPassword } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../firebase/config";
import { finishSmsSignIn, sendSignInSms, smsErrorMessage } from "../../utils/firebaseSms";

type LoginResult = {
  requiresTwoFactor?: boolean;
  requiresSms?: boolean;
  challengeToken?: string;
  customToken?: string;
};

function errorMessage(error: unknown, fallback: string) {
  if (error && typeof error === "object" && "message" in error) {
    const message = String((error as { message?: string }).message || "");
    if (message) return message;
  }
  return fallback;
}

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [smsResolver, setSmsResolver] = useState<MultiFactorResolver | null>(null);
  const [smsHint, setSmsHint] = useState<MultiFactorInfo | null>(null);
  const [smsVerificationId, setSmsVerificationId] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [smsResendIn, setSmsResendIn] = useState(0);
  const [offerChoice, setOfferChoice] = useState(false);
  const [picked, setPicked] = useState<"" | "sms" | "authenticator">("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (smsResendIn <= 0) return undefined;
    const timer = window.setTimeout(() => setSmsResendIn((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [smsResendIn]);

  const finishSignIn = async (customToken: string) => {
    await signInWithCustomToken(auth, customToken);
    navigate("/dashboard");
  };

  const startSms = async () => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/dashboard");
    } catch (smsError) {
      const smsErrorCode = smsError && typeof smsError === "object" && "code" in smsError
        ? String((smsError as { code?: string }).code)
        : "";
      if (smsErrorCode !== "auth/multi-factor-auth-required") throw smsError;
      const resolver = getMultiFactorResolver(auth, smsError as never);
      const hint = resolver.hints.find((item) => item.factorId === "phone") || resolver.hints[0];
      if (!hint || hint.factorId !== "phone") {
        throw new Error("Text message is not available for this account");
      }
      setSmsResolver(resolver);
      setSmsHint(hint);
      setSmsVerificationId(await sendSignInSms(auth, resolver, hint, "super-admin-recaptcha"));
      setSmsResendIn(30);
    }
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const adminLogin = httpsCallable<{ email: string; password: string }, LoginResult>(functions, "adminLogin");
      const result = await adminLogin({ email, password });
      if (result.data.requiresSms && result.data.requiresTwoFactor) {
        if (!result.data.challengeToken) {
          throw new Error("Sign-in did not complete");
        }
        setChallengeToken(result.data.challengeToken);
        setOfferChoice(true);
        setPicked("");
        return;
      }
      if (result.data.requiresTwoFactor) {
        if (!result.data.challengeToken) {
          throw new Error("Sign-in did not complete");
        }
        setOfferChoice(false);
        setPicked("authenticator");
        setChallengeToken(result.data.challengeToken);
        return;
      }
      if (result.data.requiresSms) {
        setOfferChoice(false);
        setPicked("sms");
        await startSms();
        return;
      }
      if (result.data.customToken) {
        await finishSignIn(result.data.customToken);
        return;
      }
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/dashboard");
    } catch (error) {
      toast({
        title: "Login Failed",
        description: errorMessage(error, "Invalid email or password"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const adminLogin2fa = httpsCallable<{ challengeToken: string; token: string }, LoginResult>(
        functions,
        "adminLogin2fa"
      );
      const result = await adminLogin2fa({ challengeToken, token: code });
      if (!result.data.customToken) {
        throw new Error("Sign-in did not complete");
      }
      await finishSignIn(result.data.customToken);
    } catch (error) {
      toast({
        title: "Login Failed",
        description: errorMessage(error, "Invalid authentication code"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSms = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!smsResolver || !smsVerificationId) return;
    setLoading(true);
    try {
      await finishSmsSignIn(smsResolver, smsVerificationId, smsCode);
      navigate("/dashboard");
    } catch (error) {
      toast({
        title: "Login Failed",
        description: smsErrorMessage(error),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const resendSms = async () => {
    if (!smsResolver || !smsHint || smsResendIn > 0) return;
    setLoading(true);
    try {
      setSmsVerificationId(await sendSignInSms(auth, smsResolver, smsHint, "super-admin-recaptcha"));
      setSmsCode("");
      setSmsResendIn(30);
      toast({
        title: "Text message requested",
        description: "It can take a minute to arrive. You can send it again after 30 seconds.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Text message",
        description: smsErrorMessage(error),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const chooseSms = async () => {
    setLoading(true);
    setPicked("sms");
    try {
      await startSms();
    } catch (error) {
      setPicked("");
      toast({
        title: "Text message",
        description: smsErrorMessage(error),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const phoneLabel = smsHint && "phoneNumber" in smsHint ? String((smsHint as { phoneNumber?: string }).phoneNumber || "") : "";
  const choosing = offerChoice && !picked && !smsResolver;

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="#f4fafc"
      px={4}
    >
      <VStack
        spacing={6}
        p={8}
        bg="white"
        borderRadius="lg"
        border="1px solid"
        borderColor="#d5e4ea"
        boxShadow="sm"
        w="100%"
        maxW="420px"
      >
        <Box textAlign="center">
          <Box as="img" src="/red_eagle/red_eagle_64.png" alt="" w="64px" h="64px" mx="auto" mb={3} />
          <Text fontSize="xl" fontWeight="600" letterSpacing="-0.02em" color="#023047">
            RMS POS Super Admin
          </Text>
          <Text fontSize="sm" color="gray.600" mt={1}>
            {choosing
              ? "Choose a verification method."
              : challengeToken && picked !== "sms"
              ? "Enter the 6-digit code from your authenticator app."
              : smsResolver
                ? `Enter the text code${phoneLabel ? ` sent to ${phoneLabel}` : ""}.`
                : "Sign in to manage stores, products, and billing."}
          </Text>
        </Box>
        <Box id="super-admin-recaptcha" />
        {choosing ? (
          <VStack spacing={3} w="100%">
            <Button width="100%" onClick={() => setPicked("authenticator")}>
              Authenticator
            </Button>
            <Button width="100%" variant="outline" onClick={chooseSms} isLoading={loading}>
              Text message
            </Button>
            <Button
              type="button"
              variant="ghost"
              width="100%"
              onClick={() => {
                setOfferChoice(false);
                setChallengeToken("");
                setPicked("");
              }}
            >
              Back
            </Button>
          </VStack>
        ) : smsResolver || picked === "sms" ? (
          <form onSubmit={handleSms} style={{ width: "100%" }}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Text code</FormLabel>
                <Input
                  value={smsCode}
                  onChange={(event) => setSmsCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
              </FormControl>
              <Button type="submit" width="100%" isLoading={loading} isDisabled={smsCode.length < 6 || !smsVerificationId}>
                Verify
              </Button>
              <Button
                type="button"
                variant="outline"
                width="100%"
                onClick={resendSms}
                isDisabled={smsResendIn > 0 || loading}
              >
                {smsResendIn > 0 ? `Send code again in ${smsResendIn}s` : "Send code again"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                width="100%"
                onClick={() => {
                  setSmsResolver(null);
                  setSmsHint(null);
                  setSmsVerificationId("");
                  setSmsCode("");
                  setPicked("");
                }}
              >
                Back
              </Button>
            </VStack>
          </form>
        ) : challengeToken ? (
          <form onSubmit={handleCode} style={{ width: "100%" }}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Authentication code</FormLabel>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
              </FormControl>
              <Button type="submit" width="100%" isLoading={loading} isDisabled={code.length !== 6}>
                Verify
              </Button>
              <Button
                type="button"
                variant="ghost"
                width="100%"
                onClick={() => {
                  setCode("");
                  if (offerChoice) {
                    setPicked("");
                    return;
                  }
                  setChallengeToken("");
                }}
              >
                Back
              </Button>
            </VStack>
          </form>
        ) : (
          <form onSubmit={handlePassword} style={{ width: "100%" }}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </FormControl>
              <FormControl>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </FormControl>
              <Button type="submit" width="100%" isLoading={loading}>
                Login
              </Button>
            </VStack>
          </form>
        )}
      </VStack>
    </Box>
  );
};

export default Login;
